import { useEffect, useMemo } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { App as AntApp, ConfigProvider } from 'antd';
import esES from 'antd/locale/es_ES';
import 'dayjs/locale/es';
import dayjs from 'dayjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useThemeStore } from '@/stores/themeStore';
import { buildAntdTheme } from '@/theme/antd-tokens';

import { AuthGuard, GuestGuard } from '@/auth/AuthGuard';
import { AuthLayout } from '@/layouts/AuthLayout';
import { MainLayout } from '@/layouts/MainLayout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { EnterprisesPage } from '@/pages/admin/enterprises/EnterprisesPage';
import { UsersPage } from '@/pages/admin/users/UsersPage';
import { MdmsPage } from '@/pages/admin/mdms/MdmsPage';
import { AppsPage } from '@/pages/apps/AppsPage';
import { ProfilesPage } from '@/pages/profiles/ProfilesPage';
import { DevicesPage } from '@/pages/devices/DevicesPage';
import { DeviceDetailPage } from '@/pages/devices/detail/DeviceDetailPage';
import { CommandsPage } from '@/pages/commands/CommandsPage';
import { CompliancePage } from '@/pages/compliance/CompliancePage';
import { JobsPage } from '@/pages/jobs/JobsPage';
import { EnrollmentPage } from '@/pages/enrollment/EnrollmentPage';
import { ProfilePage } from '@/pages/profile/ProfilePage';

dayjs.locale('es');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

export function App() {
  const mode = useThemeStore((s) => s.mode);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const antdTheme = useMemo(() => buildAntdTheme(mode), [mode]);

  return (
    <ConfigProvider locale={esES} theme={antdTheme}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <HashRouter>
            <Routes>
              {/* Rutas publicas (login) */}
              <Route
                element={
                  <GuestGuard>
                    <AuthLayout />
                  </GuestGuard>
                }
              >
                <Route path="/login" element={<LoginPage />} />
              </Route>

              {/* Rutas protegidas */}
              <Route
                element={
                  <AuthGuard>
                    <MainLayout />
                  </AuthGuard>
                }
              >
                <Route path="/" element={<DashboardPage />} />
                <Route path="/devices" element={<DevicesPage />} />
                <Route path="/devices/:id" element={<DeviceDetailPage />} />
                <Route path="/enrollment" element={<EnrollmentPage />} />
                <Route path="/profiles" element={<ProfilesPage />} />
                <Route path="/apps" element={<AppsPage />} />
                <Route path="/commands" element={<CommandsPage />} />
                <Route path="/compliance" element={<CompliancePage />} />
                <Route path="/admin/mdms" element={<MdmsPage />} />
                <Route path="/admin/users" element={<UsersPage />} />
                <Route path="/admin/enterprises" element={<EnterprisesPage />} />
                <Route path="/admin/jobs" element={<JobsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
              </Route>

              <Route path="/404" element={<NotFoundPage />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </HashRouter>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}
